import { useContext, useState } from 'react';

import { faCircleCheck } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@repo/videa-components/src/index';

import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ExternalPatientContext } from '../constants';
import { IframeMessageType, ModalType, TrialStatus } from '../types';

interface Props {
  isCentralizedBilling?: boolean;
  showPracticeAIImpact: boolean;
  trialStatus?: TrialStatus;
}

export const TrialExpiredCollapsible = ({
  isCentralizedBilling,
  showPracticeAIImpact,
  trialStatus,
}: Props) => {
  const [isOpen, setIsOpen] = useState(true);

  const { trackEvent } = useTrackEvent();
  const externalPatientContext = useContext(ExternalPatientContext);

  const showUpgradeRequestSuccess =
    isCentralizedBilling && trialStatus === TrialStatus.CANCEL_REQUEST_PAID;
  const showUpgradeButton =
    !isCentralizedBilling ||
    (isCentralizedBilling && trialStatus === TrialStatus.TRIAL_EXPIRED);

  return (
    <Collapsible
      className="vai-px-2 vai-bg-zinc-700 vai-text-white vai-p-3 vai-rounded-md vai-flex vai-flex-col data-[state=open]:vai-gap-2"
      onOpenChange={(open) => {
        setIsOpen(open);
        trackEvent(mixpanelEvents.externalCollapsibleToggle, {
          ...externalPatientContext,
          isOpen: open,
          source: 'TRIAL_EXPIRED',
        });
      }}
      open={isOpen}
    >
      <CollapsibleTrigger className="vai-w-full vai-flex vai-items-center vai-justify-between">
        {isOpen ? (
          <div className="vai-rounded-full vai-bg-zinc-800 vai-text-zinc-200 vai-px-3 vai-py-1 vai-uppercase vai-font-inter vai-text-[12px]">
            <Trans>Trial Expired</Trans>
          </div>
        ) : (
          <p className="vai-font-inter vai-font-semibold">
            <Trans>Get full-time AI assistance</Trans>
          </p>
        )}
        <FontAwesomeIcon icon={isOpen ? faMinus : faPlus} />
      </CollapsibleTrigger>

      <CollapsibleContent className="vai-flex vai-flex-col vai-gap-2">
        <p className="vai-font-sans vai-font-semibold">
          <Trans>Get full-time AI assistance</Trans>
        </p>
        <div
          className="vai-rounded-lg vai-overflow-hidden vai-h-28"
          style={{
            backgroundImage:
              'url(assets/images/external/image-with-findings.png)',
            backgroundSize: 'cover',
            backgroundPosition: '0 75%',
          }}
        />
        <p className="vai-font-sans vai-text-sm">
          <Trans>
            Trial ended. Continue to access all AI features by upgrading.
          </Trans>
        </p>
        {showPracticeAIImpact && (
          <Button
            className="vai-font-inter vai-text-sm vai-font-normal vai-text-blue-500 hover:vai-text-blue-500 vai-underline !vai-p-0 !vai-h-auto vai-self-start hover:vai-bg-transparent"
            onClick={() => {
              trackEvent(mixpanelEvents.externalAiImpactModalOpen, {
                ...externalPatientContext,
                source: 'TRIAL_EXPIRED',
              });
              window.parent.postMessage(
                {
                  type: IframeMessageType.MODAL_OPEN,
                  payload: {
                    type: ModalType.AI_IMPACT,
                    patientId: externalPatientContext.patientId,
                    imageId: externalPatientContext.imageId,
                    externalPatientId: externalPatientContext.externalPatientId,
                    externalImageId: externalPatientContext.externalImageId,
                  },
                },
                '*',
              );
            }}
            variant="ghost"
          >
            <Trans>View Practice AI Impact</Trans>
          </Button>
        )}

        {showUpgradeRequestSuccess && (
          <Alert variant="successSolid">
            <FontAwesomeIcon icon={faCircleCheck} />
            <AlertTitle>Upgrade Requested</AlertTitle>
            <AlertDescription>
              <Trans>
                Your account owner hasn&apos;t responded to your request to
                upgrade.
              </Trans>
            </AlertDescription>
          </Alert>
        )}

        {showUpgradeButton && (
          <Button
            className="vai-mt-2 !vai-font-medium"
            onClick={() => {
              trackEvent(mixpanelEvents.externalUpgradeModalOpen, {
                ...externalPatientContext,
                source: 'TRIAL_EXPIRED',
              });
              window.parent.postMessage(
                {
                  type: IframeMessageType.MODAL_OPEN,
                  payload: {
                    type: ModalType.UPGRADE,
                    patientId: externalPatientContext.patientId,
                    imageId: externalPatientContext.imageId,
                    externalPatientId: externalPatientContext.externalPatientId,
                    externalImageId: externalPatientContext.externalImageId,
                  },
                },
                '*',
              );
            }}
          >
            {isCentralizedBilling ? (
              <Trans>Request Upgrade to Detect AI</Trans>
            ) : (
              <Trans>Upgrade to Detect AI</Trans>
            )}
          </Button>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};
